import { prop } from "@suse-o11y/prop";
import { RemoteData } from "@suse-o11y/remote-data";
import { useMemo } from "react";

import { Permission } from "~/models/dto";

import type { DashboardReadSchema } from "../../common/openapi";
import { usePermissions } from "../../common/use-permissions";
import { useUserProfile } from "../../common/use-user-profile";

type MinimalSchema = Pick<DashboardReadSchema, "ownerId" | "name">;

interface DashboardsPermissions {
	hasCreateDashboardsPermission: boolean;
	hasUpdateDashboardsPermission: (schema: MinimalSchema) => boolean;
	hasDeleteDashboardsPermission: (schema: MinimalSchema) => boolean;
	hasGetDashboardsPermission: boolean;
}

const useDashboardsPermissions = () => {
	const permissions = usePermissions();

	const [userId] = useUserProfile({
		select: prop("id"),
	});

	return useMemo(() => {
		if (userId == null || permissions == null) {
			return RemoteData.loading();
		}

		return RemoteData.success<never, DashboardsPermissions>({
			hasCreateDashboardsPermission: permissions.hasSystemPermission(Permission.CreateDashboards),

			hasUpdateDashboardsPermission({ ownerId, name }) {
				return userId === ownerId && permissions.hasResourcePermission(name, Permission.UpdateDashboards);
			},

			hasDeleteDashboardsPermission({ ownerId, name }) {
				return userId === ownerId && permissions.hasResourcePermission(name, Permission.DeleteDashboards);
			},

			hasGetDashboardsPermission: permissions.hasSystemPermission(Permission.GetDashboards),
		});
	}, [permissions, userId]);
};

export { useDashboardsPermissions };
